import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown, ChevronUp, Calendar, DollarSign, Clock, Building2 } from 'lucide-react';
import SkeletonImage from './common/SkeletonImage';

const CaseStudiesDisplay = ({ caseStudies }) => {
  const intl = useIntl();
  const [expandedStudy, setExpandedStudy] = useState(null);

  if (!caseStudies || caseStudies.length === 0) {
    return null;
  }

  const toggleExpand = (id) => {
    setExpandedStudy(expandedStudy === id ? null : id);
  };

  // Construire l'URL complète de l'image
  const getImageUrl = (image) => {
    if (!image) return "https://via.placeholder.com/400x300?text=Case+Study";
    const imageUrl = image.formats?.large?.url || image.formats?.medium?.url || image.url;
    return imageUrl.startsWith('/') ? `${process.env.REACT_APP_STRAPI_URL}${imageUrl}` : imageUrl;
  };

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "case.studies.title" }, { default: "Use Cases" })}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {intl.formatMessage({ id: "case.studies.description" }, { default: "Discover how we've helped clients achieve their goals" })}
          </p>
        </div>

        <div className="space-y-8">
          {caseStudies.map((study) => (
            <div
              key={study.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
            >
              {/* Header - Always visible */}
              <div
                className="cursor-pointer p-6 bg-gradient-to-r from-green-700 to-teal-700 text-white"
                onClick={() => toggleExpand(study.id)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-xl md:text-2xl font-bold">{study.title}</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm bg-white/20 px-3 py-1 rounded-full hidden md:inline-block">
                      {study.marketSegment}
                    </span>
                    {expandedStudy === study.id ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
              </div>

              {/* Content - Expandable */}
              {expandedStudy === study.id && (
                <div className="p-6 animate-fadeIn">
                  <div className="flex flex-col md:flex-row gap-6 mb-6">
                    {/* Image */}
                    <div className="md:w-2/5">
                      <SkeletonImage
                        src={getImageUrl(study.image)}
                        alt={study.title}
                        className="w-full h-56 rounded-xl shadow-md"
                        imgClassName="w-full h-full object-cover rounded-xl shadow-md"
                      />
                    </div>

                    {/* Info Cards */}
                    <div className="md:w-3/5">
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                          <Building2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div>
                            <div className="text-xs text-gray-500">Client</div>
                            <div className="font-medium text-gray-900 truncate">{study.client}</div>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div>
                            <div className="text-xs text-gray-500">Period</div>
                            <div className="font-medium text-gray-900">{study.period}</div>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                          <Clock className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div>
                            <div className="text-xs text-gray-500">Duration</div>
                            <div className="font-medium text-gray-900">{study.duration}</div>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                          <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div>
                            <div className="text-xs text-gray-500">Budget</div>
                            <div className="font-medium text-gray-900">{study.budget}</div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Service / Sector</h4>
                        <p className="text-gray-600">{study.service}</p>
                      </div>
                    </div>
                  </div>

                  {/* Challenges, Solution, Benefits */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* Challenges */}
                    <div className="bg-red-50 p-5 rounded-xl border border-red-100">
                      <h4 className="text-base font-semibold text-red-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        Challenges
                      </h4>
                      <ul className="space-y-2">
                        {study.challenges && study.challenges.length > 0 ? (
                          study.challenges.map((challenge, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-red-400 mt-1 text-xs">•</span>
                              <span className="text-gray-700 text-sm">{challenge.name}</span>
                            </li>
                          ))
                        ) : (
                          <li className="text-gray-500 text-sm">No challenges listed</li>
                        )}
                      </ul>
                    </div>

                    {/* Solution */}
                    <div className="bg-blue-50 p-5 rounded-xl border border-blue-100">
                      <h4 className="text-base font-semibold text-blue-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        Solution
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {study.solution || "No solution description available"}
                      </p>
                    </div>

                    {/* Benefits */}
                    <div className="bg-green-50 p-5 rounded-xl border border-green-100">
                      <h4 className="text-base font-semibold text-green-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Benefits
                      </h4>
                      <ul className="space-y-2">
                        {study.benefits && study.benefits.length > 0 ? (
                          study.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-green-400 mt-1 text-xs">•</span>
                              <span className="text-gray-700 text-sm">{benefit.name}</span>
                            </li>
                          ))
                        ) : (
                          <li className="text-gray-500 text-sm">No benefits listed</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesDisplay;
